import { useMemo } from 'react';
import { useClientGroup } from '@/common/hooks/clientGroups/hooks/useClientGroup';
import { useAgentLicenseState } from '@/common/hooks/useAgentLicenseState';
import { useCore } from '$api-clients/hooks';

export function useBuyerAgreementData(clientGroupId: string) {
  const {
    clientGroup,
    loading: clientGroupLoading,
    error: clientGroupError,
    mutate: mutateClientGroup,
  } = useClientGroup(clientGroupId);

  const { state: agentState, loading: agentStateLoading } =
    useAgentLicenseState(clientGroup?.owner, clientGroup?.teamId);

  const {
    data: contacts,
    isLoading: contactsLoading,
    error: contactsError,
  } = useCore('/interactions/{interactionId}/contacts', {
    params: { path: { interactionId: clientGroupId } },
  });

  const agents = useMemo(
    () => (contacts ?? []).filter((c) => c.type === 'agent'),
    [contacts],
  );

  const clientEntityTypes = useMemo(
    () => clientGroup?.clients.map((c) => c.entityType) ?? [],
    [clientGroup?.clients],
  );

  const isPristine = useMemo(() => {
    if (!clientGroup) return true;

    // == null covers both null and undefined (commissionAdditionalAmount is optional in the API response)
    return (
      clientGroup.startDate == null &&
      clientGroup.endDate == null &&
      clientGroup.commissionValue == null &&
      clientGroup.priceRangeMin == null &&
      clientGroup.priceRangeMax == null &&
      clientGroup.commissionAdditionalAmount == null
    );
  }, [clientGroup]);

  return {
    agents,
    agentState,
    clientGroup,
    clientEntityTypes,
    isPristine,
    isLoading: clientGroupLoading || agentStateLoading || contactsLoading,
    error: clientGroupError ?? contactsError,
    mutateClientGroup,
  };
}
